import { Download } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  useToast,
} from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import {
  useExportLocumPayrollFeed,
  useFinanceLocumPayouts,
  useProcessLocumPayment,
  type LocumPayWeek,
} from '@/features/locum-pay/api';
import { locumPayErrorMessage } from '@/features/locum-pay/errors';
import { weekLabel } from '@/features/locum-pay/format';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState } from '@/lib/url-state';
import styles from './LocumPayPages.module.css';

export function FinanceLocumPayoutPage() {
  const { t } = useTranslation();
  const payouts = useFinanceLocumPayouts();
  const payment = useProcessLocumPayment();
  const payrollExport = useExportLocumPayrollFeed();
  const toast = useToast();
  const [payingId, setPayingId] = useOptionalUrlState('drawer');
  const paying = payouts.data?.find((row) => row.id === payingId) ?? null;
  const exportFeed = () =>
    payrollExport.mutate(undefined, {
      onSuccess: (result) =>
        toast.show({
          tone: 'success',
          title: t('locumPay.exported'),
          body: t('locumPay.exportedBody', {
            fileName: result.fileName,
            count: result.rowCount,
          }),
        }),
    });
  const exportError = isApiError(payrollExport.error)
    ? payrollExport.error
    : undefined;
  return (
    <PageContainer>
      <div className={styles.stack}>
        <Card
          title={t('locumPay.financeTitle')}
          actions={
            <Button
              size="sm"
              icon={<Download size={15} />}
              loading={payrollExport.isPending}
              onClick={exportFeed}
            >
              {t('locumPay.exportBooks')}
            </Button>
          }
          flush
        >
          <DataTable
            caption={t('locumPay.financeTitle')}
            rows={payouts.data}
            rowKey={(row) => row.id}
            loading={payouts.isLoading}
            error={payouts.error}
            onRetry={() => void payouts.refetch()}
            empty={
              <EmptyState
                title={t('locumPay.noPayouts')}
                body={t('locumPay.noPayoutsBody')}
              />
            }
            columns={[
              {
                key: 'locum',
                header: t('locumPay.locum'),
                render: (row) => (
                  <div>
                    <strong>{row.locumName}</strong>
                    <span className={styles.muted}>{row.branchName}</span>
                  </div>
                ),
              },
              {
                key: 'week',
                header: t('locumPay.week'),
                render: (row) => weekLabel(row),
              },
              {
                key: 'gross',
                header: t('locumPay.grossPay'),
                render: (row) => <strong>{formatRM(row.grossPaySen)}</strong>,
              },
              {
                key: 'status',
                header: t('locumPay.statusLabel'),
                render: (row) => (
                  <Badge tone={row.status === 'paid' ? 'success' : 'warning'}>
                    {t(`locumPay.status.${row.status}`)}
                  </Badge>
                ),
              },
              {
                key: 'reference',
                header: t('locumPay.bankReference'),
                render: (row) => row.bankReference ?? '—',
              },
              {
                key: 'actions',
                header: t('locumPay.actions'),
                render: (row) =>
                  row.status === 'approved' ? (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => setPayingId(row.id)}
                    >
                      {t('locumPay.processPayment')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
        {exportError && <ErrorState error={exportError} onRetry={exportFeed} />}
      </div>
      {paying && (
        <PaymentDialog
          row={paying}
          pending={payment.isPending}
          error={payment.error}
          onClose={() => {
            payment.reset();
            setPayingId(null);
          }}
          onSubmit={(bankReference) =>
            payment.mutate(
              { id: paying.id, bankReference },
              {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: t('locumPay.paid') });
                  setPayingId(null);
                },
              },
            )
          }
        />
      )}
    </PageContainer>
  );
}

function PaymentDialog({
  row,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  row: LocumPayWeek;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (bankReference: string) => void;
}) {
  const { t } = useTranslation();
  const [reference, setReference] = useState('');
  const [attempted, setAttempted] = useState(false);
  const apiError = isApiError(error) ? error : undefined;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (reference.trim()) onSubmit(reference.trim());
  };
  return (
    <Dialog
      open
      title={t('locumPay.paymentTitle', { name: row.locumName })}
      onClose={onClose}
      dismissible={!pending}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            form="process-locum-payment"
            variant="primary"
            loading={pending}
          >
            {t('locumPay.confirmPayment')}
          </Button>
        </>
      }
    >
      <form id="process-locum-payment" onSubmit={submit}>
        <Field
          label={t('locumPay.bankReference')}
          hint={t('locumPay.bankReferenceHint')}
          error={
            apiError?.code === 'BANK_REFERENCE_REQUIRED'
              ? locumPayErrorMessage(apiError, t)
              : attempted && !reference.trim()
                ? t('locumPay.bankReferenceRequired')
                : undefined
          }
        >
          <Input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        </Field>
        {apiError && (
          <ErrorState
            error={apiError}
            body={locumPayErrorMessage(apiError, t)}
          />
        )}
      </form>
    </Dialog>
  );
}
